import { useMutation, useQueryClient } from '@tanstack/react-query';
import Bluebird from 'bluebird';
import { useTenantApi } from '../../../hooks/useTenantApi';
import { useToast } from '../../../hooks/useToast';
import { engageLibraryKeys } from './engage-library-query-keys';
import {
  EngageLibraryBatchedSyncResponse,
  EngageLibrarySyncRequest,
  EngageLibrarySyncResponse,
} from './engage-library-types';
import { syncTenantEngageLibrary } from './tenant-engage-library-api';
import { describeSyncSummary, summarizeSyncResults } from './engage-library-utils';

const UNKNOWN_ERROR_MESSAGE = 'An unexpected error occurred';
const MAX_CONCURRENT_SYNC_BATCHES = 3;

const errorMessage = (reason: unknown): string =>
  reason instanceof Error && reason.message ? reason.message : UNKNOWN_ERROR_MESSAGE;

function toBatchedResponse(
  settledRequests: PromiseSettledResult<EngageLibrarySyncResponse>[],
): EngageLibraryBatchedSyncResponse {
  const responses = settledRequests.flatMap(settled =>
    settled.status === 'fulfilled' ? [settled.value] : [],
  );
  const failedRequestIndexes = settledRequests.flatMap((settled, index) =>
    settled.status === 'rejected' ? [index] : [],
  );
  const firstFailure = settledRequests.find(settled => settled.status === 'rejected');
  return {
    results: responses.flatMap(response => response.results),
    jobIds: responses.flatMap(response => (response.jobId ? [response.jobId] : [])),
    failedRequestIndexes,
    failureMessage: firstFailure ? errorMessage(firstFailure.reason) : undefined,
  };
}

export function useSyncTenantEngageLibrary() {
  const { tenantId } = useTenantApi();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requests: EngageLibrarySyncRequest[]) => {
      const settledRequests = await Bluebird.map(
        requests,
        async (request): Promise<PromiseSettledResult<EngageLibrarySyncResponse>> => {
          try {
            const value = await syncTenantEngageLibrary(tenantId, request);
            return { status: 'fulfilled', value };
          } catch (reason) {
            return { status: 'rejected', reason };
          }
        },
        { concurrency: MAX_CONCURRENT_SYNC_BATCHES },
      );
      const everyRequestFailed = settledRequests.every(settled => settled.status === 'rejected');
      if (everyRequestFailed) throw new Error(errorMessage(settledRequests[0]?.reason));
      return toBatchedResponse(settledRequests);
    },
    onSuccess: (data, requests) => {
      const failedRequestCount = data.failedRequestIndexes.length;
      const summary = summarizeSyncResults(data.results);
      const summaryDescription = describeSyncSummary(summary);
      const hasFailures = failedRequestCount > 0 || summary.failures.length > 0;
      let title = 'Changes applied';
      if (failedRequestCount > 0) title = 'Some changes were not saved';
      else if (summary.failures.length > 0) title = 'Some changes were blocked';
      else if (summary.queuedCount > 0) title = 'Changes queued';

      const descriptions = [summaryDescription];
      if (failedRequestCount > 0) {
        descriptions.push(
          `${failedRequestCount} of ${requests.length} batches failed and are still pending: ${data.failureMessage}`,
        );
      }
      addToast({
        title,
        description: descriptions.filter(Boolean).join(' · '),
        variant: hasFailures ? 'destructive' : 'success',
      });
      // Awaited so the mutation stays pending until the refetched library replaces the saved
      // checkbox changes; otherwise rows flash back to their old state.
      return queryClient.invalidateQueries({ queryKey: engageLibraryKeys.library(tenantId) });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Unable to update library',
        description: error.message || UNKNOWN_ERROR_MESSAGE,
        variant: 'destructive',
      });
    },
  });
}
